import React, { useState } from "react";
import Layout from "../../../Layouts/Layout";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useForm } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { SelectStyles } from "../../../styles/ReactSelect";

// Tradesman-side configuration for Tradesman Domestic: which trades they
// take domestic work in, their free primary postcode district, and paid
// extra districts (£10/month each, quantity-billed via Stripe).
const ProfileSetup = ({
  profile,
  profile_trades,
  primary_postcode_district,
  extra_postcodes,
  subscription,
  trades,
  monthly_price_per_postcode,
}) => {
  const { data, setData, put, processing } = useForm({
    active: profile ? profile.active : true,
    primary_postcode_district_id: profile
      ? profile.primary_postcode_district_id
      : null,
    trade_ids: (profile_trades || []).map((t) => t.id),
  });

  const [extraPostcodeChoice, setExtraPostcodeChoice] = useState(null);

  const tradeOptions = trades.map((t) => ({ value: t.id, label: t.name }));

  const districtLabel = (d) =>
    d.region ? `${d.code} — ${d.region}` : d.code;

  const loadDistricts = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) {
      callback([]);
      return;
    }
    fetch(
      `/domestic/postcode_districts?q=${encodeURIComponent(inputValue)}`,
      { headers: { Accept: "application/json" } }
    )
      .then((r) => r.json())
      .then((districts) =>
        callback(
          districts.map((d) => ({ value: d.id, label: districtLabel(d) }))
        )
      )
      .catch(() => callback([]));
  };

  function submit(e) {
    e.preventDefault();
    put("/domestic/tradesman_profile");
  }

  function addExtraPostcode() {
    if (!extraPostcodeChoice) return;
    Inertia.post("/domestic/postcode_subscriptions", {
      postcode_district_id: extraPostcodeChoice.value,
    });
    setExtraPostcodeChoice(null);
  }

  function removeExtraPostcode(subscriptionId) {
    Inertia.delete(`/domestic/postcode_subscriptions/${subscriptionId}`);
  }

  function resumeCheckout() {
    Inertia.post("/domestic/postcode_subscriptions/checkout");
  }

  return (
    <Layout title="Tradesman Domestic — My Profile">
      <div className="mt-6 max-w-2xl space-y-8">
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Trades you take domestic work in
            </label>
            <p className="text-xs text-gray-500 mb-1">
              You will only see customer requests matching these trades.
            </p>
            <Select
              isMulti
              options={tradeOptions}
              styles={SelectStyles}
              value={tradeOptions.filter((o) =>
                data.trade_ids.includes(o.value)
              )}
              onChange={(selected) =>
                setData(
                  "trade_ids",
                  (selected || []).map((o) => o.value)
                )
              }
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Primary postcode district (free)
            </label>
            <p className="text-xs text-gray-500 mb-1">
              e.g. KT22 — you will see customer requests in this district at
              no cost.
            </p>
            <AsyncSelect
              styles={SelectStyles}
              loadOptions={loadDistricts}
              defaultOptions={[]}
              placeholder="Search a postcode district…"
              defaultValue={
                primary_postcode_district
                  ? {
                      value: primary_postcode_district.id,
                      label: districtLabel(primary_postcode_district),
                    }
                  : null
              }
              onChange={(selected) =>
                setData(
                  "primary_postcode_district_id",
                  selected ? selected.value : null
                )
              }
            />
          </div>

          <button
            type="submit"
            disabled={processing || !data.primary_postcode_district_id}
            className="inline-flex justify-center rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 disabled:opacity-50"
          >
            {profile ? "Save profile" : "Activate Tradesman Domestic"}
          </button>
        </form>

        {profile && (
          <div className="border-t border-gray-200 pt-6">
            <h2 className="text-lg font-medium text-gray-900">
              Extra postcode districts
            </h2>
            <p className="text-sm text-gray-500">
              £{monthly_price_per_postcode}/month per extra district — a
              separate Tradesman Domestic subscription, independent of any
              other subscription on your account.
            </p>

            {subscription && subscription.has_pending && !subscription.paid && (
              <div className="mt-4 rounded-md bg-yellow-50 border border-yellow-200 p-4">
                <p className="text-sm text-yellow-800">
                  Some districts are awaiting payment — they will not be
                  active until checkout is complete.
                </p>
                <button
                  type="button"
                  onClick={resumeCheckout}
                  className="mt-2 rounded-md bg-orange-600 py-2 px-4 text-sm font-medium text-white hover:bg-orange-700"
                >
                  Complete payment
                </button>
              </div>
            )}

            {subscription &&
              !subscription.paid &&
              !subscription.has_pending &&
              extra_postcodes.length > 0 && (
                <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-800">
                    Your Domestic subscription is {subscription.status} —
                    extra districts are paused until payment is up to date.
                  </p>
                </div>
              )}

            <ul className="mt-4 divide-y divide-gray-200">
              {extra_postcodes.map((pc) => (
                <li
                  key={pc.id}
                  className="flex items-center justify-between py-3"
                >
                  <span className="text-sm text-gray-900">
                    {pc.code}
                    {pc.region ? ` — ${pc.region}` : ""}
                    {pc.status === "pending" && (
                      <span className="ml-2 inline-flex rounded-full bg-yellow-100 px-2 text-xs font-semibold leading-5 text-yellow-800">
                        awaiting payment
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeExtraPostcode(pc.id)}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Remove
                  </button>
                </li>
              ))}
              {extra_postcodes.length === 0 && (
                <li className="py-3 text-sm text-gray-500">
                  No extra districts yet.
                </li>
              )}
            </ul>

            <div className="mt-4 flex items-end gap-3">
              <div className="flex-1">
                <AsyncSelect
                  styles={SelectStyles}
                  loadOptions={loadDistricts}
                  defaultOptions={[]}
                  placeholder="Add another district…"
                  value={extraPostcodeChoice}
                  onChange={setExtraPostcodeChoice}
                />
              </div>
              <button
                type="button"
                onClick={addExtraPostcode}
                disabled={!extraPostcodeChoice}
                className="rounded-md border border-transparent bg-orange-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-orange-700 disabled:opacity-50"
              >
                Add (£{monthly_price_per_postcode}/mo)
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ProfileSetup;
